import { Component, Output, EventEmitter, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

export interface UploadedFile {
  file: File;
  name: string;
  format: string;
  size: number;
  progress: number;
  uploaded: boolean;
}

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule
  ]
})
export class FileUploadComponent {
  @Input() title: string = 'File upload';
  @Input() accept: string = '*/*';
  @Input() multiple: boolean = false;
  @Input() maxFileSize: number = 10485760; // 10MB default
  @Input() isRemoveDisabled: boolean = false;

  @Output() filesSelected = new EventEmitter<File[]>();
  @Output() fileRemoved = new EventEmitter<UploadedFile>();
  @Output() uploadComplete = new EventEmitter<UploadedFile[]>();

  uploadedFiles: UploadedFile[] = [];
  isDragOver: boolean = false;

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.uploadedFiles.length === 0) {
      this.isDragOver = true;
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (this.uploadedFiles.length > 0 && !this.multiple) {
      return;
    }

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFiles(Array.from(files));
    }
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFiles(Array.from(input.files));
    }
  }

  handleFiles(files: File[]): void {
    files.forEach(file => {
      if (file.size > this.maxFileSize) {
        console.error(`File ${file.name} exceeds maximum size`);
        return;
      }

      const uploadedFile: UploadedFile = {
        file: file,
        name: file.name,
        format: this.getFileFormat(file.name),
        size: file.size,
        progress: 0,
        uploaded: false
      };

      this.uploadedFiles.push(uploadedFile);
      this.simulateUpload(uploadedFile);
    });

    this.filesSelected.emit(files);
  }

  simulateUpload(uploadedFile: UploadedFile): void {
    // Simulate upload progress
    const interval = setInterval(() => {
      if (uploadedFile.progress < 100) {
        uploadedFile.progress += 10;
      } else {
        uploadedFile.uploaded = true;
        clearInterval(interval);
        this.checkAllUploaded();
      }
    }, 200);
  }

  checkAllUploaded(): void {
    if (this.uploadedFiles.every(f => f.uploaded)) {
      this.uploadComplete.emit(this.uploadedFiles);
    }
  }

  removeFile(file: UploadedFile): void {
    const index = this.uploadedFiles.indexOf(file);
    if (index > -1) {
      this.uploadedFiles.splice(index, 1);
      this.fileRemoved.emit(file);
    }
  }

  getFileFormat(filename: string): string {
    const extension = filename.split('.').pop();
    return extension ? extension.toUpperCase() : 'FILE';
  }

  getFileIcon(format: string): string {
    const lowerFormat = format.toLowerCase();

    // Document formats
    if (['pdf'].includes(lowerFormat)) {
      return 'pi pi-file-pdf';
    }
    if (['doc', 'docx'].includes(lowerFormat)) {
      return 'pi pi-file-word';
    }
    if (['xls', 'xlsx'].includes(lowerFormat)) {
      return 'pi pi-file-excel';
    }

    // Image formats
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(lowerFormat)) {
      return 'pi pi-image';
    }

    // Video formats
    if (['mp4', 'avi', 'mov', 'mkv'].includes(lowerFormat)) {
      return 'pi pi-video';
    }

    // Default
    return 'pi pi-file';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  openFileDialog(): void {
    if (this.uploadedFiles.length > 0 && !this.multiple) {
      return;
    }
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    fileInput?.click();
  }
}

